import * as XLSX from 'xlsx';
import { bulkImportPatients, getAllPatients } from './firestore';

// Function to parse an Excel file and return the patient data
export const parseExcelFile = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // Get the first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        
        // Convert the worksheet to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Map Excel column names to our patient schema
        const mappedData = jsonData.map((row: any) => ({
          name: row['name'] || row['Name'] || '',
          treatmentType: row['treatmentType'] || row['Treatment Type'] || '',
          adherenceRate: parseFloat(row['adherenceRate'] || row['Adherence Rate'] || 0),
          missedDoses: parseInt(row['missedDoses'] || row['Missed Doses'] || 0),
          confirmedDoses: parseInt(row['confirmedDoses'] || row['Confirmed Doses'] || 0),
          pendingDoses: parseInt(row['pendingDoses'] || row['Pending Doses'] || 0),
          startDate: row['startDate'] || row['Start Date'] || '',
          nextAppointment: row['nextAppointment'] || row['Next Appointment'] || '',
          municipality: row['municipality'] || row['Municipality'] || '',
          province: row['province'] || row['Province'] || '',
          age: parseInt(row['age'] || row['Age'] || 0),
          sex: row['sex'] || row['Sex'] || '',
          civilStatus: row['civilStatus'] || row['Civil Status'] || '',
          philHealthNumber: row['philHealthNumber'] || row['PhilHealth Number'] || '',
          phoneNumber: row['phoneNumber'] || row['Phone Number'] || ''
        }));
        
        resolve(mappedData);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    // Read the file as binary
    reader.readAsBinaryString(file);
  });
};

// Function to import Excel data to Firestore
export const importExcelToFirestore = async (file: File): Promise<{ 
  success: boolean; 
  message: string; 
  count?: number;
  updatedCount?: number;
  addedCount?: number;
}> => {
  try {
    // Parse the Excel file
    const patients = await parseExcelFile(file);
    
    if (patients.length === 0) {
      return {
        success: false,
        message: 'No patient data found in the Excel file.'
      };
    }
    
    // Get existing patients to count updates vs. additions
    const existingPatients = await getAllPatients();
    
    // Count how many will be updates vs. new additions
    let updatedCount = 0;
    let addedCount = 0;
    
    patients.forEach(patient => {
      const exists = existingPatients.some(
        existing => 
          existing.name === patient.name && 
          existing.philHealthNumber === patient.philHealthNumber
      );
      
      if (exists) {
        updatedCount++;
      } else {
        addedCount++;
      }
    });
    
    // Import the patients to Firestore
    await bulkImportPatients(patients);
    
    return {
      success: true,
      message: `Successfully imported ${patients.length} patients (${updatedCount} updated, ${addedCount} added).`,
      count: patients.length,
      updatedCount,
      addedCount
    };
  } catch (error) {
    console.error('Error importing Excel to Firestore:', error);
    return {
      success: false,
      message: `Error importing data: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

// Create a named export object
const excelImportService = {
  parseExcelFile,
  importExcelToFirestore
};

export default excelImportService; 